import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Typography, Chip, TextField, Select, MenuItem,
  InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Tooltip, IconButton, Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { taskAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { downloadCsv } from '../../utils/exportCsv';

export const TASK_STATUS = {
  planning:  { color: '#4472C4', label: 'Planning' },
  active:    { color: '#2E7D32', label: 'Active' },
  on_hold:   { color: '#E65100', label: 'On Hold' },
  completed: { color: '#0277BD', label: 'Completed' },
  cancelled: { color: '#C62828', label: 'Cancelled' },
};

const fmtDate = (d) => {
  if (!d) return '—';
  const p = d.split('-');
  const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${parseInt(p[2], 10)} ${m[parseInt(p[1], 10) - 1]} ${p[0]}`;
};

export default function TasksPage() {
  const navigate = useNavigate();
  const { isSupportTeam, isAdmin } = useAuth();
  const { showToast } = useToast();
  const canCreate = isSupportTeam() || isAdmin();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(() => new URLSearchParams(window.location.search).get('status') || '');

  const load = useCallback(() => {
    setLoading(true);
    taskAPI.getAll()
      .then((res) => setTasks(res.data || []))
      .catch(() => showToast('Failed to load tasks', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const isOverdue = (t) => t.end_date && !['completed', 'cancelled'].includes(t.status) && new Date(t.end_date) < new Date();

  const displayed = tasks.filter((t) => {
    if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (status === 'overdue') return isOverdue(t);
    if (status === 'open') return !['completed', 'cancelled'].includes(t.status);
    if (status) return t.status === status;
    return true;
  });

  const counts = Object.keys(TASK_STATUS).reduce((acc, s) => ({ ...acc, [s]: tasks.filter((t) => t.status === s).length }), {});

  const handleDownload = () => {
    if (!displayed.length) return showToast('No tasks to download', 'error');
    downloadCsv(
      `tasks-${new Date().toISOString().slice(0, 10)}.csv`,
      ['#', 'Task', 'Status', 'Team Members', 'Start Date', 'End Date', 'TAT Date', 'Latest Remark', 'Remark By', 'Details'],
      displayed.map((t, i) => [
        i + 1, t.title, TASK_STATUS[t.status]?.label || t.status,
        (t.members || []).map((m) => m.member?.name).filter(Boolean).join(', '),
        t.start_date || '', t.end_date || '', t.tat_date || '',
        t.latest_remark?.remark || '', t.latest_remark?.author || '', t.description || '',
      ])
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>Tasks</Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 0.3 }}>
            In-house work that is not part of a client project · {displayed.length} task{displayed.length === 1 ? '' : 's'}
          </Typography>
        </Box>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/tasks/new')} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            New Task
          </Button>
        )}
      </Box>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {Object.entries(TASK_STATUS).map(([k, s]) => (
          <Chip key={k} size="small" onClick={() => setStatus(status === k ? '' : k)}
            label={`${s.label} ${counts[k] || 0}`}
            sx={{ fontWeight: 700, border: '1px solid', borderColor: status === k ? s.color : s.color + '40',
              bgcolor: status === k ? s.color + '18' : 'transparent', color: s.color }} />
        ))}
      </Stack>

      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder="Search tasks…" value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }} />
            <Select size="small" value={status} displayEmpty sx={{ minWidth: 150 }} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="">All Status</MenuItem>
              {Object.entries(TASK_STATUS).map(([k, s]) => <MenuItem key={k} value={k}>{s.label}</MenuItem>)}
              <MenuItem value="open">Open (not completed)</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </Select>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ fontWeight: 700 }}>
              Download
            </Button>
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}><CircularProgress /></Box>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#F4F7FB', fontWeight: 700, color: '#1E3A5F', fontSize: '0.75rem', py: 1.5, whiteSpace: 'nowrap' } }}>
                  <TableCell width={40}>#</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                  <TableCell>TAT</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Latest Remark</TableCell>
                  <TableCell align="center" width={60}>View</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.map((t, i) => {
                  const st = TASK_STATUS[t.status] || TASK_STATUS.planning;
                  const names = (t.members || []).map((m) => m.member?.name).filter(Boolean);
                  return (
                    <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/tasks/${t.id}`)}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{t.title}</Typography>
                        {t.description && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{t.description}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip label={st.label} size="small" sx={{ bgcolor: st.color + '15', color: st.color, fontWeight: 700, border: `1px solid ${st.color}30` }} />
                        {isOverdue(t) && <Chip label="Overdue" size="small" color="error" variant="outlined" sx={{ ml: 0.5, height: 20, fontSize: '0.62rem' }} />}
                      </TableCell>
                      <TableCell>
                        {names.length === 0 ? <Typography variant="body2" color="text.disabled">Unassigned</Typography>
                          : <Typography variant="body2">{names.slice(0, 2).join(', ')}{names.length > 2 ? ` +${names.length - 2}` : ''}</Typography>}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(t.start_date)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(t.end_date)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(t.tat_date)}</TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        {t.latest_remark ? (
                          <Tooltip title={`${t.latest_remark.author || ''}: ${t.latest_remark.remark}`}>
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {t.latest_remark.author}{t.remark_count > 1 ? ` (${t.remark_count})` : ''}
                              </Typography>
                              <Typography variant="body2" color="text.secondary"
                                sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: '0.78rem' }}>
                                {t.latest_remark.remark}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/tasks/${t.id}`); }}>
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {displayed.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <AssignmentIcon sx={{ fontSize: 40, opacity: 0.2, display: 'block', mx: 'auto', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {canCreate ? 'No tasks found. Click "New Task" to create one.' : 'No tasks are assigned to you.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
}
